<template>
  <ValidationObserver
    v-slot="{handleSubmit}"
    ref="formObserver"
  >
    <b-modal
      id="factModal"
      v-model="factModal.show"
      :title="`${form.entityId ? 'Edit' : 'Create'} Fact Table`"
      @hidden="hideModal"
      @ok.prevent="handleSubmit(onSubmit)"
      @hide="beforeHide"
      @show="showModal"
    >
      <content-spinner
        :show="factModal.loading"
        text="Please wait..."
      />
      <b-form
        v-if="!factModal.loading"
        @submit="handleSubmit(onSubmit)"
        @reset="handleSubmit(onReset)"
        @keydown.enter.prevent="handleSubmit(onSubmit)"
      >
        <input-widget
          ref="focusedField"
          :model="form"
          :field="fields.entityDisplayName"
          :autofocus="true"
        />
        <input-widget
          :model="form"
          :field="fields.description"
        />
        <input-widget
          :model="form"
          :field="fields.tags"
        />
        <!--        <input-widget :model="form" :field="fields.changeType"/>-->
        <!--        <input-widget :model="form" :field="fields.deletionType"/>-->
        <input-widget
          :model="form"
          :field="fields.isOnPhysicalModel"
        />
        <input-widget
          :model="form"
          :field="fields.isOnLogicalModel"
        />
      </b-form>
      <template #modal-footer="{ok, cancel}">
        <modal-footer
          v-if="!form.entityId"
          v-model="addAnother"
          :loading="factModal.loading"
          :cancel="cancel"
          :ok="ok"
        />
      </template>
    </b-modal>
  </ValidationObserver>
</template>

<script>

import {createNamespacedHelpers} from 'vuex';
import ContentSpinner from '../../core/components/ContentSpinner';
import {ValidationObserver, ValidationProvider, extend} from 'vee-validate';
import {required} from 'vee-validate/dist/rules';
import InputWidget from '../../core/components/complex-table/components/InputWidget';
import ModalFooter from './components/ModalFooter';
import {v4 as uuidv4} from 'uuid';

extend('required', {
  ...required,
  message: 'The {_field_} field is required.',
});

const {mapState, mapGetters, mapActions} = createNamespacedHelpers('sunModel');
export default {
  name: 'FactModal',
  components: {ModalFooter, InputWidget, ContentSpinner, ValidationObserver, ValidationProvider},
  data() {
    return {
      form: {
        entityDisplayName: '',
        description: '',
        tags: [],
        // changeType: '',
        // deletionType: '',
        isOnPhysicalModel: true,
        isOnLogicalModel: true,
      },
      addAnother: false,
      fields: {
        entityDisplayName: {
          type: 'text',
          required: true,
          label: 'Name',
          name: 'entityDisplayName',
        },
        description: {
          type: 'textarea',
          required: false,
          label: 'Description',
          name: 'description',
        },
        tags: {
          type: 'multi-select',
          required: false,
          name: 'tags',
          config: {
            optionValue: 'value',
            optionLabel: 'name',
            placeholder: 'Facts Tags',
            multiple: true,
            taggable: false,
          },
          data: {
            options: [],
          },
        },
        /*changeType: {
          type: 'radio-group',
          required: false,
          label: 'Change Type',
          name: 'changeType',
          data: {
            options: [
              {text: 'Type 0', value: 'type0'},
              {text: 'Type 1', value: 'type1'},
              {text: 'Type 2', value: 'type2'},
            ]
          }
        },*/
        /*deletionType: {
          type: 'radio-group',
          required: false,
          label: 'Deletion Type',
          name: 'deletionType',
          data: {
            options: [
              {text: 'Implicit', value: 'implicit'},
              {text: 'Explicit', value: 'explicit'},
            ]
          }
        },*/
        isOnPhysicalModel: {
          type: 'checkbox',
          required: false,
          label: 'Include On Physical Model',
          name: 'isOnPhysicalModel',
        },
        isOnLogicalModel: {
          type: 'checkbox',
          required: false,
          label: 'Include On Logical Model',
          name: 'isOnLogicalModel',
        },
      },
    };
  },
  watch: {
    tags: {
      immediate: true,
      deep: true,
      handler() {
        this.fields.tags.data.options = this.tags.data.map(t => ({name: t.tagName, value: t.tagId}));
      },
    },
  },
  computed: {
    ...mapState(['factModal', 'tags']),
    ...mapGetters(['tagsAsMap']),
  },
  methods: {
    ...mapActions(['hideFactModal', 'createFactTable', 'updateFactTable']),
    beforeHide(e) {
      if (this.factModal.loading) {
        e.preventDefault();
        return false;
      }
    },
    async onSubmit(e) {
      // e.preventDefault();
      let params = {...this.form};
      let action = 'create';
      if (params.entityId) {
        params = {
          entityId: params.entityId,
          entityType: params.entityType,
          entityDisplayName: params.entityDisplayName,
          description: params.description ? params.description : '',
          tags: JSON.stringify(params.tags.map(t => t.value)),
          isOnPhysicalModel: params.isOnPhysicalModel,
          isOnLogicalModel: params.isOnLogicalModel,
        };
        action = 'update';
      } else {
        params.entityId = uuidv4();
        params.entityType = 'fact';
        action = 'create';
      }
      // delete params.tags

      let res;
      if (action === 'create') {
        res = await this.createFactTable(params);
      } else {
        res = await this.updateFactTable(params);
      }
      if (res.success) {
        if (!this.addAnother) {
          this.hideModal();
        }
        this.$toasted.success('Fact record saved successfully', {
          duration: TOASTER_DURATION,
        });
        this.resetModal();
        this.$refs.focusedField.$refs.currentInput.focus();
      } else {
        this.$toasted.error(res.body.ErrorMessage, {
          duration: TOASTER_DURATION,
        });
      }
    },
    resetModal() {
      this.form = {
        entityDisplayName: '',
        description: '',
        tags: [],
        // changeType: '',
        // deletionType: '',
        isOnPhysicalModel: true,
        isOnLogicalModel: true,
      };
      this.$refs.formObserver.reset();
    },
    hideModal() {
      this.addAnother = false;
      this.hideFactModal();
    },
    showModal() {
      this.form = {
        ...this.factModal.data,
        tags: this.factModal.data.tags.map(tagId => {
          const tag = this.tagsAsMap.get(tagId) || {};
          return {name: tag.tagName, value: tagId};
        }),
      };
    },
    onReset() {

    },
  },
};
</script>

<style lang="scss">
#factModal {
  /deep/ .lobi-spinner {
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    background-color: white;
    z-index: 1;
  }
}
</style>
